import React, { useCallback, useState, useRef, useMemo, useEffect } from 'react'
import styled, { css } from 'styled-components'
import { borderRadius, colorSystem, fontSystem } from '@/styles/theme'
import { Icon } from '@musinsa/mwms-ds'
import Text from '@/components/content/Text'
import { SelectBoxProps } from '@/components/content/SelectBox'
import useClickOutside from '@/hooks/useClickOutside'

interface SearchableSelectBoxProps<T> extends Omit<SelectBoxProps<T>, 'onClick'> {
  disabled?: boolean
  /** 전체 옵션의 표기. 기본 '전체' (SelectBox_new 와 같은 이름의 prop) */
  allLabel?: string
  /** 선택 영역 높이(px). 옆에 놓이는 SearchableMultiSelect 와 맞추라고 열어 둔다. 기본 28 */
  triggerHeight?: number
}

const ALL_VAL = ''

function SearchableSelectBox<T extends string | number>({
  block,
  className,
  placeholder,
  value,
  disabled,
  options = [],
  hasAll = false,
  allLabel = '전체',
  triggerHeight = 28,
  onChange,
}: SearchableSelectBoxProps<T>) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [focusedIndex, setFocusedIndex] = useState<number>(-1)

  const containerRef = useRef<HTMLDivElement>(null)
  const optionRefs = useRef<Array<HTMLDivElement | null>>([])

  const isDisabled = disabled ?? false

  const toggleDropdown = useCallback(() => {
    if (isDisabled) return
    setIsOpen((prev) => !prev)
  }, [isDisabled])

  const handleSelect = useCallback(
    (value: T) => {
      if (!onChange || isDisabled) return
      onChange(value)
      handleOptionClose()
    },
    [onChange, isDisabled],
  )

  const handleOptionClose = useCallback(() => {
    setSearchTerm('')
    setFocusedIndex(-1)
    setIsOpen(false)
  }, [])

  const filteredOptions = useMemo(() => options.filter((opt) => opt.label.toLowerCase().includes(searchTerm.toLowerCase())), [options, searchTerm])
  const selectedOption = useMemo(() => options.find((opt) => opt.value === value), [options, value])

  useClickOutside(containerRef, handleOptionClose)

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (!isOpen || isDisabled) return

      const totalOptions = hasAll ? filteredOptions.length + 1 : filteredOptions.length

      if (e.key === 'ArrowDown' || e.key === 'Tab') {
        e.preventDefault()
        setFocusedIndex((prev) => (prev + 1) % totalOptions)
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setFocusedIndex((prev) => (prev - 1 + totalOptions) % totalOptions)
      } else if (e.key === 'Enter' && focusedIndex !== -1) {
        e.preventDefault()
        const selected = hasAll && focusedIndex === 0 ? (ALL_VAL as T) : filteredOptions[hasAll ? focusedIndex - 1 : focusedIndex].value
        handleSelect(selected)
      } else if (e.key === 'Escape') {
        e.preventDefault()
        handleOptionClose()
      }
    },
    [filteredOptions, focusedIndex, hasAll, isOpen, isDisabled],
  )

  useEffect(() => {
    if (focusedIndex !== -1 && optionRefs.current[focusedIndex]) {
      optionRefs.current[focusedIndex]?.scrollIntoView({ block: 'nearest' })
    }
  }, [focusedIndex])

  // 빈 값을 전체 옵션 라벨로 보여 주는 건 전체 옵션이 있을 때만 — 없으면 아직 안 고른 것이라 placeholder 를 띄운다
  const displayLabel = selectedOption?.label || (hasAll && value === '' && allLabel) || placeholder || '값을 선택해주세요'

  return (
    <SelectBoxContainer ref={containerRef} className={className} $block={block} $disabled={isDisabled} onKeyDown={handleKeyDown} tabIndex={0}>
      <SelectBoxDisplay $isOpen={isOpen} onClick={toggleDropdown} title={displayLabel} $height={triggerHeight}>
        <Text kind="Body_14_regular" color={colorSystem.text.common.primary}>
          {displayLabel}
        </Text>
      </SelectBoxDisplay>
      <InputIcon name={isOpen ? 'arrow-up-filled' : 'arrow-down-filled'} onClick={toggleDropdown} size={20} color="gray600" />
      {isOpen && (
        <DropdownContainer>
          <SearchInput
            autoFocus
            type="text"
            placeholder="검색..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value)
              setFocusedIndex(0)
            }}
            onClick={(e) => e.stopPropagation()}
          />
          <OptionsList>
            {hasAll && (
              <Option
                ref={(el) => {
                  optionRefs.current[0] = el
                }}
                onClick={() => handleSelect(ALL_VAL as T)}
                tabIndex={-1}
                $focused={focusedIndex === 0}
              >
                <Text kind="Body_14_bold">{allLabel}</Text>
              </Option>
            )}
            {filteredOptions.map((opt, idx) => {
              const refIndex = hasAll ? idx + 1 : idx
              return (
                <Option
                  key={`select-box-opt-${opt.value}`}
                  ref={(el) => {
                    optionRefs.current[refIndex] = el
                  }}
                  onClick={() => handleSelect(opt.value)}
                  tabIndex={-1}
                  $focused={focusedIndex === refIndex}
                >
                  <Text kind="Body_14_regular">{opt.label}</Text>
                </Option>
              )
            })}
          </OptionsList>
        </DropdownContainer>
      )}
    </SelectBoxContainer>
  )
}

const SelectBoxContainer = styled.div<{ $block?: boolean; $disabled?: boolean }>`
  position: relative;
  background-color: ${colorSystem.bg.input.enabled};
  ${borderRadius.sm};

  ${({ $block }) =>
    $block &&
    css`
      width: 100%;
    `}

  ${({ $disabled }) =>
    $disabled &&
    css`
      background-color: ${colorSystem.bg.input.disabled};
      pointer-events: none;
    `}
`

const SelectBoxDisplay = styled.div<{ $isOpen?: boolean; $height: number }>`
  cursor: pointer;
  /* 긴 라벨(카테고리 전체경로 + 코드)이 박스 밖으로 흘러 아래 줄에 찍히던 것을 고친다.
     선택값에 따라 높이가 늘면 옆 필드와 어긋나 보이므로 호출부가 준 높이로 고정하고,
     넘치는 만큼은 잘라 낸다 (전문은 title 툴팁) */
  height: ${({ $height }) => $height}px;
  padding: 2px 8px;
  ${borderRadius.sm};

  display: flex;
  align-items: center;
  overflow: hidden;

  > * {
    white-space: normal;
    word-break: break-word;
  }

  ${({ $isOpen }) =>
    $isOpen
      ? css`
          border: 1px solid ${colorSystem.border.element_active};
        `
      : css`
          border: 1px solid ${colorSystem.border.element};
        `}
`

const DropdownContainer = styled.div`
  position: absolute;
  top: calc(100% + 2px);
  left: 0;
  right: 0;
  border: 1px solid ${colorSystem.border.element};
  background: white;
  z-index: 1000;
  padding: 4px 8px;
  ${borderRadius.sm};
`

const SearchInput = styled.input`
  width: 100%;
  padding: 6px;
  border: none;
  border-bottom: 1px solid ${colorSystem.border.element};
  ${fontSystem.Body_14_regular};
`

const OptionsList = styled.div`
  max-height: 320px;
  overflow-y: auto;
`
const Option = styled.div<{ $focused?: boolean }>`
  width: 100%;
  padding: 8px;
  cursor: pointer;
  background-color: ${({ $focused }) => ($focused ? colorSystem.bg.common.secondary_hover : 'transparent')};

  &:hover {
    ${borderRadius.sm};
    background-color: ${colorSystem.bg.common.secondary_hover};
  }
`

const InputIcon = styled(Icon)`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(calc(-50%));
  z-index: 1;
`

export default SearchableSelectBox
